import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Header, Stat, StatLabel, StatNumber } from '~/shared/ui'

/**
 * Card payments, and how many people make them.
 *
 * The Deposits section directly above already reports card *money* and the
 * per-rail attempt funnel. What neither answers is how many people the rail
 * actually serves — and $40,000 through the card rail is a different business
 * decision depending on whether it came from forty players or from four.
 *
 * Two rows, kept apart on purpose: the top row counts transactions and the
 * bottom row counts people. A grid where some tiles are payments and others are
 * players, with nothing saying which is which, is the sort of table that gets
 * quoted wrongly in a meeting once and then never trusted again.
 */

type MoonpayUsage = {
    transactions: { total: number; credited: number; awaiting: number; failed: number }
    users: { attempted: number; paid: number; repeat: number; only_failed: number }
    usd: number
    /** Null rather than zero when nothing has been credited — no data, not a zero average. */
    avg_usd: number | null
    per_paying_user: number | null
}

const usd = (value: number | null) =>
    value === null || value === undefined
        ? '—'
        : `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const count = (value?: number) => (value === undefined ? undefined : value.toLocaleString('en-US'))

export const MoonpayUsage = () => {
    const [data, setData] = useState<MoonpayUsage>()

    useEffect(() => {
        jsonrpc('statistic.deposits.moonpay').then((res) => setData(res.result))
    }, [])

    const loading = data === undefined

    return (
        <Box className='w-full text-center gap-2'>
            <Header>Card payments (MoonPay)</Header>
            <small className='text-white/50'>
                Every card payment MoonPay/Helio reported to us, and the people who made them. Read from the MoonPay
                table rather than the ledger — a payment that failed never reaches the ledger, so the people it turned
                away are invisible everywhere else on this page. A payment we never heard about is not here.
            </small>

            <Box className='w-full gap-2 pt-2'>
                <small className='text-white/50'>Transactions</small>
                <section className='grid grid-cols-2 sm:grid-cols-4 gap-2 w-full'>
                    <Stat.Gray loading={loading}>
                        <StatLabel className='text-amber-500'>All attempts</StatLabel>
                        <StatNumber>{count(data?.transactions.total)}</StatNumber>
                    </Stat.Gray>
                    <Stat.Gray
                        loading={loading}
                        title='Credited to the player. The dollars are what the ledger was credited (floored to 4 places), not what Helio reported.'
                    >
                        <StatLabel className='text-green-500'>Credited</StatLabel>
                        <StatNumber>{count(data?.transactions.credited)}</StatNumber>
                        <StatLabel className='text-white/40'>{loading ? undefined : usd(data.usd)}</StatLabel>
                    </Stat.Gray>
                    <Stat.Gray loading={loading}>
                        <StatLabel className='text-white/60'>Awaiting</StatLabel>
                        <StatNumber>{count(data?.transactions.awaiting)}</StatNumber>
                    </Stat.Gray>
                    <Stat.Gray
                        loading={loading}
                        title='Not credited: confirmed by Helio but refused here (wrong currency, unknown scale), plus any Helio reported as failed.'
                    >
                        <StatLabel className={data?.transactions.failed ? 'text-rose-400' : 'text-white/60'}>
                            Failed
                        </StatLabel>
                        <StatNumber>{count(data?.transactions.failed)}</StatNumber>
                    </Stat.Gray>
                </section>
            </Box>

            <Box className='w-full gap-2 pt-4'>
                <small className='text-white/50'>People — these count players, not payments</small>
                <section className='grid grid-cols-2 sm:grid-cols-4 gap-2 w-full'>
                    <Stat.Gray loading={loading} title='Anybody who has ever started a card payment.'>
                        <StatLabel className='text-amber-500'>Tried</StatLabel>
                        <StatNumber>{count(data?.users.attempted)}</StatNumber>
                    </Stat.Gray>
                    <Stat.Gray loading={loading} title='Anybody who has had a card payment credited.'>
                        <StatLabel className='text-green-500'>Paid</StatLabel>
                        <StatNumber>{count(data?.users.paid)}</StatNumber>
                        <StatLabel className='text-white/40'>
                            {loading ? undefined : `${usd(data.per_paying_user)} each`}
                        </StatLabel>
                    </Stat.Gray>
                    <Stat.Gray loading={loading} title='Paid by card more than once — the rail being used again.'>
                        <StatLabel className='text-green-500'>Came back</StatLabel>
                        <StatNumber>{count(data?.users.repeat)}</StatNumber>
                    </Stat.Gray>
                    {/* The figure worth acting on: people who tried to give us
                        money and could not. It is a subtraction the reader
                        should not have to do. */}
                    <Stat.Gray loading={loading} title='Tried to pay by card and never had one credited.'>
                        <StatLabel className={data?.users.only_failed ? 'text-rose-400' : 'text-white/60'}>
                            Never got through
                        </StatLabel>
                        <StatNumber>{count(data?.users.only_failed)}</StatNumber>
                    </Stat.Gray>
                </section>
                <small className='text-white/40'>
                    Average credited payment {loading ? '—' : usd(data.avg_usd)}. “Tried” includes everyone in the
                    other three tiles.
                </small>
            </Box>
        </Box>
    )
}
